// skipcq: JS-W1028, JS-0128
import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import BeerIngestionStyleTypeStrip from '../BeerIngestionStyleTypeStrip';
import {
  resolveCatalogStyleId,
  unknownScrapedStyles,
} from '../../../utils/beerStyle/scrapedBeerImport';

function styleTypeLabel(importedStyle, styleMapping, styleTypeByName) {
  const catalogId = resolveCatalogStyleId(importedStyle, styleMapping);
  if (catalogId) {
    return 'In catalog';
  }
  return styleTypeByName[importedStyle] || 'Needs type';
}

/**
 * ScrapedBeerTable
 * Displays scraped beers. Unknown extracted styles get a type from beerStyleTypes.
 */
function ScrapedBeerTable({
  beers,
  styleMapping,
  styleTypes,
  styleTypeByName,
  onAssignStyleType,
  onSelectionChange,
}) {
  const [selectedIndices, setSelectedIndices] = useState(
    () => new Set([...(beers || []).keys()]),
  );
  const [selectionBeers, setSelectionBeers] = useState(beers);
  if (beers !== selectionBeers) {
    setSelectionBeers(beers);
    setSelectedIndices(new Set([...(beers || []).keys()]));
  }
  const onSelectionChangeRef = useRef(onSelectionChange);

  useEffect(() => {
    onSelectionChangeRef.current = onSelectionChange;
  }, [onSelectionChange]);

  useEffect(() => {
    const initialIndices = [...(beers || []).keys()];
    if (onSelectionChangeRef.current && initialIndices.length > 0) {
      onSelectionChangeRef.current(initialIndices);
    }
  }, [beers]);

  const handleToggleSelect = useCallback(
    (index) => {
      setSelectedIndices((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(index)) {
          newSet.delete(index);
        } else {
          newSet.add(index);
        }
        if (onSelectionChangeRef.current) {
          onSelectionChangeRef.current(Array.from(newSet));
        }
        return newSet;
      });
    },
    [],
  );

  const handleSelectAll = useCallback(() => {
    if (!beers || beers.length === 0) return;
    const allIndices = new Set([...beers.keys()]);
    setSelectedIndices(allIndices);
    if (onSelectionChangeRef.current) {
      onSelectionChangeRef.current(Array.from(allIndices));
    }
  }, [beers]);

  const handleDeselectAll = useCallback(() => {
    setSelectedIndices(new Set());
    if (onSelectionChangeRef.current) {
      onSelectionChangeRef.current([]);
    }
  }, []);

  const batchCount = useMemo(() => {
    if (!beers || beers.length === 0) return 0;
    return Math.ceil(beers.length / 100);
  }, [beers]);

  const batchRanges = useMemo(() => {
    if (!beers || beers.length === 0 || batchCount === 0) return [];
    const ranges = [];
    for (let batchIndex = 0; batchIndex < batchCount; batchIndex += 1) {
      const startIndex = batchIndex * 100;
      const endIndex = Math.min(startIndex + 99, beers.length - 1);
      ranges.push({
        batchNumber: batchIndex + 1,
        startIndex,
        endIndex,
        displayStart: startIndex + 1,
        displayEnd: endIndex + 1,
      });
    }
    return ranges;
  }, [beers, batchCount]);

  const handleSelectBatch = useCallback(
    (batchNumber) => {
      if (!beers || beers.length === 0) return;
      const range = batchRanges.find((item) => item.batchNumber === batchNumber);
      if (!range) return;

      const batchIndices = new Set();
      for (let index = range.startIndex; index <= range.endIndex; index += 1) {
        batchIndices.add(index);
      }

      setSelectedIndices(batchIndices);
      if (onSelectionChangeRef.current) {
        onSelectionChangeRef.current(Array.from(batchIndices));
      }
    },
    [beers, batchRanges],
  );

  const allSelected =
    beers && beers.length > 0 && selectedIndices.size === beers.length;
  const someSelected =
    selectedIndices.size > 0 && selectedIndices.size < beers.length;

  const rowHandlers = useMemo(() => {
    if (!beers || beers.length === 0) return {};
    const handlers = {};
    for (let beerIndex = 0; beerIndex < beers.length; beerIndex += 1) {
      handlers[beerIndex] = () => handleToggleSelect(beerIndex);
    }
    return handlers;
  }, [beers, handleToggleSelect]);

  const batchHandlers = useMemo(() => {
    const handlers = {};
    batchRanges.forEach((range) => {
      handlers[range.batchNumber] = () => handleSelectBatch(range.batchNumber);
    });
    return handlers;
  }, [batchRanges, handleSelectBatch]);

  const unknownStyles = useMemo(
    () => unknownScrapedStyles(beers, styleMapping, styleTypeByName),
    [beers, styleMapping, styleTypeByName],
  );

  if (!beers || beers.length === 0) {
    return (
      <Typography variant="body2" sx={{
        color: "text.secondary"
      }}>No beers found in the scraped data.
              </Typography>
    );
  }

  return (
    <Box>
      <BeerIngestionStyleTypeStrip
        unknownStyles={unknownStyles}
        onAssignStyleType={onAssignStyleType || undefined}
        styleTypes={styleTypes}
      />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
          Beers ({beers.length})
        </Typography>
        <Typography variant="body2" sx={{
          color: "text.secondary"
        }}>
          {selectedIndices.size} of {beers.length} selected
        </Typography>
      </Box>
      {beers.length > 100 && batchRanges.length > 0 && (
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          {batchRanges.map((range) => (
            <Button
              key={range.batchNumber}
              variant="outlined"
              size="small"
              onClick={batchHandlers[range.batchNumber]}
              sx={{
                textTransform: 'none',
              }}
            >
              Select {range.displayStart}-{range.displayEnd}
            </Button>
          ))}
        </Box>
      )}
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: 400,
          mt: 2,
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={someSelected}
                  checked={allSelected}
                  onChange={allSelected ? handleDeselectAll : handleSelectAll}
                />
              </TableCell>
              <TableCell>Beer Name</TableCell>
              <TableCell>Style</TableCell>
              <TableCell>ABV</TableCell>
              <TableCell>Type</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {beers.map((beer, index) => {
              const importedStyle = (beer.beer_style || '').trim();
              const catalogId = resolveCatalogStyleId(importedStyle, styleMapping);
              const assignedType = styleTypeByName[importedStyle];
              const isReady = Boolean(catalogId || assignedType);
              const beerKey = `${beer.beer_name || ''}-${importedStyle}-${beer.beer_abv || ''}-${index}`;
              const isSelected = selectedIndices.has(index);

              return (
                <TableRow
                  key={beerKey}
                  hover
                  selected={isSelected}
                  sx={{
                    backgroundColor: isReady
                      ? undefined
                      : 'rgba(211, 47, 47, 0.1)',
                    '&:hover': {
                      backgroundColor: isReady
                        ? undefined
                        : 'rgba(211, 47, 47, 0.15)',
                    },
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isSelected}
                      onChange={rowHandlers[index]}
                    />
                  </TableCell>
                  <TableCell>{beer.beer_name ?? '-'}</TableCell>
                  <TableCell>{importedStyle || '-'}</TableCell>
                  <TableCell>{beer.beer_abv ?? '-'}</TableCell>
                  <TableCell>
                    {styleTypeLabel(importedStyle, styleMapping, styleTypeByName)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

ScrapedBeerTable.propTypes = {
  beers: PropTypes.arrayOf(
    PropTypes.shape({
      beer_name: PropTypes.string,
      beer_style: PropTypes.string,
      beer_abv: PropTypes.string,
      beer_description: PropTypes.string,
    }),
  ).isRequired,
  styleMapping: PropTypes.object,
  styleTypes: PropTypes.arrayOf(PropTypes.object),
  styleTypeByName: PropTypes.object,
  onAssignStyleType: PropTypes.func,
  onSelectionChange: PropTypes.func,
};

ScrapedBeerTable.defaultProps = {
  styleMapping: {},
  styleTypes: [],
  styleTypeByName: {},
  onSelectionChange: null,
};

export default ScrapedBeerTable;
